'use client'

import { Portal } from '@chakra-ui/react/portal'
import { AbsoluteCenter } from '@chakra-ui/react'
import {
  Menu as ChakraMenu,
  MenuArrowProps,
  MenuCheckboxItemProps,
  MenuContentProps,
  MenuItemGroupProps,
  MenuItemProps,
  MenuRadioItemProps,
  MenuTriggerItemProps,
} from '@chakra-ui/react/menu'
import * as React from 'react'
import { BsCheck, BsChevronRight } from 'react-icons/bs'

export const MenuContent = React.forwardRef(function MenuContent(
  props: MenuContentProps & { portalled?: boolean; portalRef?: React.RefObject<HTMLDivElement | null> },
  ref?: React.Ref<HTMLDivElement>,
) {
  const { portalled = true, portalRef, ...rest } = props
  return (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraMenu.Positioner>
        <ChakraMenu.Content ref={ref} {...rest} />
      </ChakraMenu.Positioner>
    </Portal>
  )
})

export const MenuArrow = React.forwardRef(function MenuArrow(props: MenuArrowProps, ref?: React.Ref<HTMLDivElement>) {
  return (
    <ChakraMenu.Arrow ref={ref} {...props}>
      <ChakraMenu.ArrowTip />
    </ChakraMenu.Arrow>
  )
})

export const MenuCheckboxItem = React.forwardRef(function MenuCheckboxItem(
  props: MenuCheckboxItemProps,
  ref?: React.Ref<HTMLDivElement>,
) {
  return (
    <ChakraMenu.CheckboxItem ps="8" ref={ref} {...props}>
      <AbsoluteCenter axis="horizontal" insetStart="4" asChild>
        <ChakraMenu.ItemIndicator>
          <BsCheck />
        </ChakraMenu.ItemIndicator>
      </AbsoluteCenter>
      {props.children}
    </ChakraMenu.CheckboxItem>
  )
})

export const MenuRadioItem = React.forwardRef(function MenuRadioItem(
  props: MenuRadioItemProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const { children, ...rest } = props
  return (
    <ChakraMenu.RadioItem ps="8" ref={ref} {...rest}>
      <AbsoluteCenter axis="horizontal" insetStart="4" asChild>
        <ChakraMenu.ItemIndicator>
          <BsCheck />
        </ChakraMenu.ItemIndicator>
      </AbsoluteCenter>
      <ChakraMenu.ItemText>{children}</ChakraMenu.ItemText>
    </ChakraMenu.RadioItem>
  )
})

export const MenuItemGroup = React.forwardRef(function MenuItemGroup(
  props: MenuItemGroupProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const { title, children, ...rest } = props
  return (
    <ChakraMenu.ItemGroup ref={ref} {...rest}>
      {title && <ChakraMenu.ItemGroupLabel userSelect="none">{title}</ChakraMenu.ItemGroupLabel>}
      {children}
    </ChakraMenu.ItemGroup>
  )
})

export const MenuTriggerItem = React.forwardRef(function MenuTriggerItem(
  props: MenuTriggerItemProps & { startIcon?: React.ReactNode; value: string },
  ref: React.Ref<HTMLDivElement>,
) {
  const { startIcon, children, ...rest } = props
  return (
    <ChakraMenu.TriggerItem ref={ref} {...rest}>
      {startIcon}
      {children}
      <BsChevronRight />
    </ChakraMenu.TriggerItem>
  )
})

export const MenuRadioItemGroup = ChakraMenu.RadioItemGroup
export const MenuContextTrigger = ChakraMenu.ContextTrigger
export const MenuRoot = ChakraMenu.Root
export const MenuSeparator = ChakraMenu.Separator

export const MenuItem = React.forwardRef(function MenuItem(props: MenuItemProps, ref: React.Ref<HTMLDivElement>) {
  return (
    <ChakraMenu.Item
      {...props}
      ref={ref}
      css={{
        // need to do this for some reason
        '&[data-highlighted]': {
          backgroundColor: 'var(--mix-background, var(--chakra-colors-bg-emphasized))',
        },
      }}
    />
  )
})

export const MenuItemText = ChakraMenu.ItemText
export const MenuItemCommand = ChakraMenu.ItemCommand
export const MenuTrigger = ChakraMenu.Trigger
export const MenuPositioner = ChakraMenu.Positioner
